/**
 * UpcomingRenewalsPanel — coach-console widget.
 *
 * Shows every active commitment rolling into open-ended weekly billing
 * in the next 7 days. Each row is one tap into the lead's dossier so
 * the coach can reach out (text/email/call) before they decide to cancel.
 *
 * Sachin spec 2026-02-28 — "lets you proactively reach out to anyone you
 * sense might churn." Same data the reminder cron uses, exposed for the
 * coach's eyes a step earlier.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    CalendarClock, ChevronRight, Mail, CheckCircle2, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relTime(iso) {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    const d = Math.floor(ms / 86400000);
    if (d < 1) return "today";
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    return `${mo}mo ago`;
}

function endsLabel(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "short", month: "short", day: "numeric",
        });
    } catch { return iso; }
}

export default function UpcomingRenewalsPanel() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/upcoming-renewals?days=7`, { withCredentials: true });
                if (!cancel) setRows(r.data?.rows || []);
            } catch (_e) {
                if (!cancel) setRows([]);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, []);

    if (loading) {
        return (
            <div className="border border-zinc-900 bg-zinc-950/70 rounded-md p-4 flex items-center gap-2 text-xs text-zinc-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading upcoming renewals…
            </div>
        );
    }

    return (
        <div
            data-testid="upcoming-renewals-panel"
            className="border border-amber-700/40 bg-amber-900/5 rounded-md"
        >
            <div className="flex items-center justify-between px-4 py-3 border-b border-amber-900/30">
                <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-amber-200 font-semibold">
                        Rolling to weekly · next 7 days
                    </span>
                </div>
                <span
                    data-testid="upcoming-renewals-count"
                    className="text-[10px] tracking-[0.2em] uppercase text-amber-300 font-semibold"
                >
                    {rows.length}
                </span>
            </div>

            {rows.length === 0 ? (
                <div className="px-4 py-5 text-xs text-zinc-400 leading-relaxed">
                    No commitments are wrapping up in the next 7 days. Members in the
                    middle of their commitment can&apos;t cancel, so the next batch will
                    surface here automatically.
                </div>
            ) : (
                <ul className="divide-y divide-amber-900/20">
                    {rows.map((r) => (
                        <li
                            key={r.commitment_id}
                            data-testid={`upcoming-renewal-${r.commitment_id}`}
                            className="px-4 py-2.5 flex items-center gap-3 hover:bg-amber-900/10 cursor-pointer transition-colors group"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/coach?lead=${r.user_id}`)}
                            onKeyDown={(e) => { if (e.key === "Enter") navigate(`/coach?lead=${r.user_id}`); }}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-white truncate font-medium">
                                        {r.parent_name}
                                    </span>
                                    {r.reminder_sent_at && (
                                        <span
                                            title="Heads-up email already sent"
                                            className="inline-flex items-center gap-0.5 text-[9px] tracking-[0.2em] uppercase text-emerald-300"
                                        >
                                            <CheckCircle2 className="w-2.5 h-2.5" />
                                            Notified
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-zinc-400 mt-0.5 flex flex-wrap items-center gap-x-2">
                                    <span>{r.display_name}</span>
                                    <span>· ends {endsLabel(r.commitment_ends_at)}</span>
                                    {r.days_until_renewal != null && (
                                        <span className={r.days_until_renewal <= 2 ? "text-red-300" : "text-amber-300"}>
                                            · {r.days_until_renewal === 0 ? "today" : `${r.days_until_renewal}d`}
                                        </span>
                                    )}
                                    {r.last_contacted_at && (
                                        <span title={`Last contacted ${relTime(r.last_contacted_at)}`}>
                                            · last touch {relTime(r.last_contacted_at)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {r.email && (
                                <a
                                    href={`mailto:${r.email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Send an email"
                                    className="inline-flex items-center justify-center w-7 h-7 text-zinc-400 hover:text-brand-red border border-transparent hover:border-zinc-700 transition-colors"
                                >
                                    <Mail className="w-3.5 h-3.5" />
                                </a>
                            )}
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-amber-300 flex-shrink-0" />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
